import React, { useImperativeHandle, useMemo, useRef, useState } from "react";
import { RCCGrid, RCCHookToast, RCCInput, RCCRefModalProps, RCCServicesApi } from "react-common-components";
import { useForm } from "react-hook-form";
import DeleteModal from "src/components/DeleteModal";
import Modal from "src/components/Modal";
import Tabs from "src/components/Tabs";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import Permissions from "src/components/Permissions";
import { simplePermissionType } from "src/components/Permissions/interface";
import TabAnalysisGeneral from "src/pages/Analysis/Panel/Components/ModalAnalysis/tabGeneral";
import FormAnalysisModel from "src/pages/Analysis/Panel/Components/ModalAnalysis/formModel";
import { useAuth } from "src/context/Auth/context";
import IComponentIcon from "react-common-components/dist/components/Icon/interface";
import ModalSide from "src/components/Modal/styles";
import { IMatba001Form, IMatba006, IModalAnalysis, IRefAnalysisModal } from "../../interface";
import { addAndChangeSchemaAnalysis, addAndChangeSchemaAnalysisModel, deleteSchemaAnalysis } from "../../schemas";

const ModalAnalysisPanel: React.ForwardRefRenderFunction<IRefAnalysisModal, IModalAnalysis> = ({ 
	onCallbackAdd,
	onCallbackChange,
	onCallbackDelete
}, ref) => {

	const { translate } = useTranslation();
	const { addToast } = RCCHookToast();
	const { user } = useAuth();

	const [selectedAnalysis, setSelectedAnalysis] = useState<IMatba001Form>({} as IMatba001Form);
	const [isAdd, setIsAdd] = useState(false);
	const [formModel, setFormModel] = useState(false);
	const [isView, setIsView] = useState(false);
	const [disabled, setDisabled] = useState(false);

	const modalAddChange = useRef<RCCRefModalProps>(null);
	const modalDelete = useRef<RCCRefModalProps>(null);

	const { 
		handleSubmit, 
		register,
		unregister,
		control, 
		reset,
		setError: setErrorAddChange,
		watch,
		setValue,
		formState: {errors: errorAddChange},
		clearErrors } = useForm();

	const { 
		handleSubmit: handleSubmitDelete, 
		register: registerDelete, 
		reset : resetDelete, 
		setError : setErrorDelete, 
		formState: { errors : errorsDelete }, 
		clearErrors: clearErrorsDelete 
	} = useForm();
	

	const actionButtonAdd = () => {
		reset();
		setIsAdd(true);
		setFormModel(false);
		setSelectedAnalysis(analysisDefault());
		modalAddChange.current?.openModal();
	};

	const actionButtonChange = (matba001: IMatba001Form, view: boolean) => {
		reset();
		setDisabled(view);
		setIsView(view);
		setIsAdd(false);
		queryDataAnalysis(matba001.maa001Id);
	};

	const actionButtonDelete = (matba001: IMatba001Form) => {
		resetDelete();
		setSelectedAnalysis(matba001);
		modalDelete.current?.openModal();
	};

	useImperativeHandle(ref, () => ({
		actionButtonAdd,
		actionButtonChange,
		actionButtonDelete
	}));

	const queryDataAnalysis = async (id: number) => {
		try{
			const { data } = await RCCServicesApi.getRequest({
				method: "Analysis/Select",
				urlParameters: {
					id
				}
			});

			setFormModel(data.maa001Maa001Id !== null);
			setSelectedAnalysis(data);
			modalAddChange.current?.openModal();
		}catch(err){
			standardErrorMessages(err, translate("Erro ao carregar dados"), addToast);
		}
	};

	const onSubmit = async (data: any) => {
		try{
			clearErrors();
			modalAddChange.current?.setLoadingButton();

			const dataForm = {
				...data,
				maa001Ameaca: data.maa001Ameaca ? "S" : "N",
				maa001Forca: data.maa001Forca ? "S" : "N",
				maa001Fraqueza: data.maa001Fraqueza ? "S" : "N",
				maa001Oportunidade: data.maa001Oportunidade ? "S" : "N",
				maa001Gut: data.maa001Gut ? "S" : "N",
				maa001Graficoradar: data.maa001Graficoradar ? "S" : "N",
				maa001Adicionanivel1: data.maa001Adicionanivel1 ? "S" : "N",
				maa001Ativo: data.maa001Ativo ? "S": "N",
				maa001Analisemodelo: data.maa001Analisemodelo ? "S" : "N",
				maa001Maa001Id: data.maa001Maa001Id === 0 ? undefined : data.maa001Maa001Id 
			};

			if(data.matba006 && data.matba006.length > 0){

				dataForm.matba006 = data.matba006.filter((dt: any) => dt.maa006Efa005Id !== 0 && dt.maa006Efa004Id !== 0);

				dataForm.matba006 = dataForm.matba006.map((maa006: any) => {
					if(typeof maa006.maa006Efa005Id === "object")
						return {
							...maa006,
							maa006Permissao: Number(maa006.maa006Permissao),
							maa006Efa005Id: String(maa006.maa006Efa005Id.value)
						};
					if(typeof maa006.maa006Efa004Id === "object")
						return {
							...maa006,
							maa006Permissao: Number(maa006.maa006Permissao),
							maa006Efa004Id: String(maa006.maa006Efa004Id.value)
						};
					return maa006;
				});
			}

			if(dataForm.maa001Maa001Id !== undefined){
				await addAndChangeSchemaAnalysisModel(translate).validate({...dataForm},{
					abortEarly: false
				});
			}else{
				await addAndChangeSchemaAnalysis(translate).validate({...dataForm},{
					abortEarly: false
				});
			}

			// verifica se tem permissão de Visializar e Editar
			if(!dataForm.matba006?.filter((x:any) => Number(x.maa006Permissao) === Number(simplePermissionType.VIEW_AND_EDIT)).length){
				modalAddChange.current?.resetLoadingButton();
				addToast({
					color: "yellow",
					description: translate("Ao menos uma permissão deve ser (Visualizar e Editar)"),
					title: translate("Atenção")
				});
				return;
			}

			if(isAdd){
				await RCCServicesApi.postRequest({
					method: "Analysis/Insert",
					body: {...dataForm}
				});

				if(onCallbackAdd)
					onCallbackAdd();

			}else{
				await RCCServicesApi.postRequest({
					method: "Analysis/Update",
					body: {...dataForm}
				});

				if(onCallbackChange)
					onCallbackChange(dataForm);
			}

			addToast({
				color: "green",
				description: isAdd ? translate("Análise adicionada com sucesso") : translate("Análise alterada com sucesso"),
				title: translate("Sucesso")
			});

			modalAddChange.current?.resetLoadingButton();
			modalAddChange.current?.closeModal();

		}catch(err: any){
			modalAddChange.current?.resetLoadingButton();
			if(getValidationErrors(err, setErrorAddChange)){
				addToast({
					title: translate("Erro"),
					description: translate("Verifique os erros no formulário e tente novamente"),
					color: "yellow"
				});
				return;
			}
			standardErrorMessages(err, isAdd ? translate("Erro ao adicionar análise") : translate("Erro ao alterar análise"), addToast);
		}
	};

	const submitDelete = async (data: any) => {
		try{
			clearErrorsDelete();	
			modalDelete.current?.setLoadingButton();

			await deleteSchemaAnalysis(translate).validate({...data, "maa001TituloConfirmation": selectedAnalysis.maa001Titulo},{
				abortEarly: false
			});
			
			await RCCServicesApi.deleteRequest({
				method: "Analysis/Delete",
				urlParameters: {
					id: selectedAnalysis.maa001Id,
					title: selectedAnalysis.maa001Titulo
				}
			});

			addToast({
				color: "green",
				description: translate("Análise excluída com sucesso"),
				title: translate("Sucesso")
			});
			modalDelete.current?.resetLoadingButton();
			modalDelete.current?.closeModal();

			if(onCallbackDelete)
				onCallbackDelete(selectedAnalysis.maa001Id);

		}catch(err){
			modalDelete.current?.resetLoadingButton();

			if(getValidationErrors(err, setErrorDelete))
				return;

			standardErrorMessages(err, translate("Erro ao excluir análise"), addToast);
		}
		
	};

	const onChangeAnalysisModel = (e: any, matba006: IMatba006[]) => {
		reset();
		clearErrors();
		unregister();

		const permissions = matba006.map((maa006: any) => {
			if(typeof maa006.maa006Efa005Id === "object")
				return {
					...maa006,
					maa006Permissao: Number(maa006.maa006Permissao),
					maa006Efa005Id: maa006.maa006Efa005Id.value,
					maa006Efa005Nome: maa006.maa006Efa005Id.label
				};
			if(typeof maa006.maa006Efa004Id === "object")
				return {
					...maa006,
					maa006Permissao: Number(maa006.maa006Permissao),
					maa006Efa004Id: maa006.maa006Efa004Id.value,
					maa006Efa004Nome: maa006.maa006Efa004Id.label
				};
			return maa006;
		});

		if(e === null){
			setSelectedAnalysis({
				...selectedAnalysis,
				maa001Titulo: "",
				maa001Descricao: "",
				maa001Maa001Id: 0,
				matba006: permissions
			});

			setFormModel(false);
		}else{
			const analysis = e.extendedProps as IMatba001Form;

			setSelectedAnalysis({
				...selectedAnalysis,
				maa001Titulo: e.label,
				maa001Descricao: analysis.maa001Descricao,
				maa001Maa001Id: e.value,
				matba006: permissions
			});

			setValue("maa001Maa001Id", e.value);
			setValue("maa001Titulo", e.label);
			setValue("maa001Descricao", analysis.maa001Descricao);

			setFormModel(true);
		}
	};

	const forms = useMemo(() => (
		<>
			{
				formModel ? 
					<FormAnalysisModel 
						disabled={disabled}
						isAdd={isAdd}
						data={selectedAnalysis}
						control={control}
						register={register}
						unregister={unregister}
						error={errorAddChange}
						clearErrors={clearErrors}
						watch={watch}
						setValue={setValue}
						onChangeAnalysisModel={onChangeAnalysisModel}
					/>

					:

					<Tabs 
						name="tabAnalysis"
						tabInit={0}
						options={[
							{
								label: translate("Geral"),
								content: 
									<TabAnalysisGeneral
										isDisabled={disabled}
										isAdd={isAdd}
										data={selectedAnalysis}
										register={register}
										unregister={unregister}
										control={control}
										error={errorAddChange}
										watch={watch}
										setValue={setValue}
										onChangeAnalysisModel={onChangeAnalysisModel}
									/>
							},{
								label: translate("Permissões"),
								content: 
									<>
										<RCCGrid
											container	
											item
											direction="column"
											alignItems="center"
											justifyContent="center"
										>
											<RCCGrid 
												container
												item
												xs={12}
												sm={10}
												alignItems="center"
												justify="flex-end"
											>
												<Permissions
													disabled={disabled}
													register={register}
													unregister={unregister}
													control={control}
													error={errorAddChange}
													clearErrors={clearErrors}
													permissionsNames= {{
														nameObject: "matba006",
														nameOfID: "maa006Id",
														nameOfSelectUser: "maa006Efa005Id",
														nameOfSelectGroup: "maa006Efa004Id",
														nameOfSelectPermission: "maa006Permissao",
														nameOfSelectGroupDescription: "maa006Efa004Nome",
														nameOfSelectUserDescription: "maa006Efa005Nome",
														defaultValue: selectedAnalysis.matba006
													}}
													permissionType="simple"
												/>
											</RCCGrid>
										</RCCGrid>
									</>
							}
						]}
					/>
			}
		</>
	), [selectedAnalysis, formModel, errorAddChange]);

	const analysisDefault = () => {
		 const analysis = {} as IMatba001Form;
		 analysis.matba006 = [{
			maa006Id: 0,
			maa006Maa001Id: 0,
			maa006Efa004Id: undefined,
			maa006Efa004Nome: "",
			maa006Efa005Id: user.efa005Id,
			maa006Efa005Nome: user.efa005Nome,
			maa006Permissao: Number(simplePermissionType.VIEW_AND_EDIT)
		}];

		return analysis;
	};

	const createHeaderModal = () => {

		const header = {} as {
			title: string;
			subtitle?: string;
			icon?: IComponentIcon;
		};

		if(isAdd){
			header.title = translate("Adicionar análise");
			header.subtitle = "";
			header.icon = {name: "plus"};
			return header;
		}

		if(isView){
			header.title = translate("Visualizar análise");
			header.subtitle = `${translate("Análise")}: ${selectedAnalysis.maa001Titulo}`;
			header.icon = {name: "eye"};
			return header;
		}

		if(!isAdd){
			header.title = translate("Alterar análise");
			header.subtitle = `${translate("Análise")}: ${selectedAnalysis.maa001Titulo}`;
			header.icon = {name: "pencil"};
			return header;
		}

		return header;
	};

	return (
		<>
			<DeleteModal
				ref={modalDelete}
				title= {translate("Exclusão de análise")}
				description= {`${translate("Deseja realmente excluir a análise")}: ${selectedAnalysis.maa001Titulo}?`}
				onSubmit = {handleSubmitDelete(submitDelete)} 
				height="250px"
			>
				<RCCGrid
					container
					item
					xs={10}
				>
					<RCCInput 
						register={registerDelete}
						name="maa001Titulo"
						label={translate("Digite o título da análise para excluí-la")}
						maxLength={200}
						error={errorsDelete.maa001Titulo}
						required
					/>
				</RCCGrid>
			</DeleteModal>

			<Modal
				ref={modalAddChange}
				onSubmit={handleSubmit(onSubmit)}
				header={createHeaderModal()}
				footer={{
					showSaveButton: !isView
				}}
				styleInline={ModalSide}
			>
				{forms}
			</Modal>
		</>
	);
};


export default React.forwardRef(ModalAnalysisPanel);

